import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { orderAPI, authAPI, productAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiCheck, FiTag, FiArrowLeft, FiRefreshCw, FiPlus, FiMapPin } from 'react-icons/fi';
import './CheckoutPage.css';

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
  'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh',
];

// ─── Payment options — Online first, COD last ─────────────────────────────────
const PAYMENT_OPTIONS = [
  {
    value: 'upi',
    label: 'UPI',
    icon: '📱',
    desc: 'GPay, PhonePe, Paytm, BHIM & more',
    badge: 'Instant',
    razorpayMethod: 'upi',
    isCod: false,
  },
  {
    value: 'card',
    label: 'Credit / Debit Card',
    icon: '💳',
    desc: 'Visa, Mastercard, RuPay accepted',
    badge: null,
    razorpayMethod: 'card',
    isCod: false,
  },
  {
    value: 'netbanking',
    label: 'Net Banking',
    icon: '🏦',
    desc: 'All major Indian banks supported',
    badge: null,
    razorpayMethod: 'netbanking',
    isCod: false,
  },

  // ── COD always last ───────────────────────────────────────────────────────
  {
    value: 'cod',
    label: 'Cash on Delivery',
    icon: '💵',
    desc: 'Pay in cash when order arrives',
    badge: null,
    razorpayMethod: null,
    isCod: true,
  },
];

// ─── Guest Auth Flow ──────────────────────────────────────────────────────────
const GuestAuthFlow = ({ onAuthComplete }) => {
  const { requestOtp, loginWithOtp, registerWithOtp } = useAuth();
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) { toast.error('Enter a valid 10-digit number'); return; }
    setLoading(true);
    try {
      const res = await requestOtp({ phone: '+91' + phone });
      setIsNewUser(res?.data?.is_new_user ?? false);
      setStep('otp');
      setResendTimer(30);
      toast.success('OTP sent!');
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      const errors = err.response?.data;
      if (errors && typeof errors === 'object') {
        Object.values(errors).flat().forEach(m => toast.error(m));
      } else {
        toast.error('Failed to send OTP');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 5) otpRefs.current[i + 1]?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) { setOtp(text.split('')); otpRefs.current[5]?.focus(); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setLoading(true);
    try {
      const user = await loginWithOtp({ phone: '+91' + phone, otp: code });
      toast.success(`Welcome back, ${user.first_name}! 👋`);
      onAuthComplete(user);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404 || isNewUser) {
        setStep('profile');
      } else {
        toast.error(err.response?.data?.error || 'Invalid OTP');
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!profile.first_name.trim()) { toast.error('Enter your first name'); return; }
    setLoading(true);
    try {
      const user = await registerWithOtp({
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        phone: '+91' + phone,
        otp: otp.join(''),
      });
      toast.success(`Welcome, ${user.first_name}! `);
      onAuthComplete(user);
    } catch (err) {
      const errors = err.response?.data;
      if (errors && typeof errors === 'object') {
        Object.values(errors).flat().forEach(m => toast.error(m));
      } else {
        toast.error('Registration failed. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await requestOtp({ phone: '+91' + phone });
      setResendTimer(30);
      setOtp(['', '', '', '', '', '']);
      toast.success('OTP sent again!');
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch { toast.error('Failed to resend OTP'); }
    finally { setLoading(false); }
  };

  return (
    <div className="checkout-guest-auth">
      {step === 'phone' && (
        <div className="guest-auth-step">
          <div className="guest-auth-icon">📱</div>
          <h2 className="guest-auth-title">Login to Continue</h2>
          <p className="guest-auth-subtitle">Enter your mobile number to proceed with checkout</p>
          <form onSubmit={handleSendOtp}>
            <div className="otp-phone-input-wrap" style={{ marginBottom: 16 }}>
              <div className="otp-phone-prefix">
                <span className="otp-flag">🇮🇳</span>
                <span>+91</span>
              </div>
              <input
                type="tel" className="otp-phone-input"
                placeholder="10-digit mobile number"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                maxLength={10} autoFocus required
              />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
              disabled={loading || phone.length !== 10}>
              {loading ? <span className="otp-btn-loading"><span className="spinner-sm" /> Sending...</span> : 'Send OTP'}
            </button>
          </form>
        </div>
      )}

      {step === 'otp' && (
        <div className="guest-auth-step">
          <button className="otp-back-btn" onClick={() => { setStep('phone'); setOtp(['','','','','','']); }}>
            <FiArrowLeft size={14} /> Edit Number
          </button>
          <div className="guest-auth-icon">🔐</div>
          <h2 className="guest-auth-title">Enter OTP</h2>
          <p className="guest-auth-subtitle">Sent to <strong>+91 {phone}</strong></p>
          <form onSubmit={handleVerifyOtp}>
            <div className="otp-boxes" onPaste={handleOtpPaste} style={{ marginBottom: 16 }}>
              {otp.map((digit, i) => (
                <input
                  key={i} ref={el => otpRefs.current[i] = el}
                  type="text" inputMode="numeric" maxLength={1}
                  className={`otp-box ${digit ? 'otp-box--filled' : ''}`}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                />
              ))}
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
              disabled={loading || otp.join('').length !== 6}>
              {loading ? <span className="otp-btn-loading"><span className="spinner-sm" /> Verifying...</span> : 'Verify & Continue'}
            </button>
          </form>
          <div className="otp-resend" style={{ marginTop: 12 }}>
            {resendTimer > 0
              ? <p className="otp-resend__timer">Resend in <strong>{resendTimer}s</strong></p>
              : <button className="otp-resend__btn" onClick={handleResend} disabled={loading}><FiRefreshCw size={13} /> Resend OTP</button>
            }
          </div>
        </div>
      )}

      {step === 'profile' && (
        <div className="guest-auth-step">
          <div className="guest-auth-icon">👤</div>
          <h2 className="guest-auth-title">Almost there!</h2>
          <p className="guest-auth-subtitle">Just a few details to create your account</p>
          <form onSubmit={handleRegister}>
            <div className="otp-name-row" style={{ marginBottom: 0 }}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">First Name *</label>
                <input className="form-input" placeholder="First name"
                  value={profile.first_name}
                  onChange={e => setProfile(p => ({ ...p, first_name: e.target.value }))}
                  required autoFocus />
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Last Name</label>
                <input className="form-input" placeholder="Last name"
                  value={profile.last_name}
                  onChange={e => setProfile(p => ({ ...p, last_name: e.target.value }))} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Email (optional)</label>
              <input className="form-input" type="email" placeholder="yourname@example.com"
                value={profile.email}
                onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
              disabled={loading || !profile.first_name.trim()}>
              {loading ? <span className="otp-btn-loading"><span className="spinner-sm" /> Creating Account...</span> : 'Continue to Checkout'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

// ─── Address Form ─────────────────────────────────────────────────────────────
const AddressForm = ({ initial = {}, onCancel, onSave, showCancel = true }) => {
  const [addr, setAddr] = useState({
    name: '', phone: '', address_line1: '', address_line2: '',
    city: '', state: '', pincode: '', ...initial,
  });
  const set = (k) => (e) => setAddr(a => ({ ...a, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!addr.name || !addr.phone || !addr.address_line1 || !addr.city || !addr.state || !addr.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    onSave(addr);
  };

  return (
    <form onSubmit={handleSubmit} className="addr-form">
      <div className="addr-form__row">
        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input className="form-input" value={addr.name} onChange={set('name')} placeholder="Full name" required />
        </div>
        <div className="form-group">
          <label className="form-label">Phone *</label>
          <input className="form-input" value={addr.phone} onChange={set('phone')} placeholder="10-digit mobile" required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Address Line 1 *</label>
        <input className="form-input" value={addr.address_line1} onChange={set('address_line1')} placeholder="House no., Street" required />
      </div>
      <div className="form-group">
        <label className="form-label">
          Address Line 2 <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span>
        </label>
        <input className="form-input" value={addr.address_line2} onChange={set('address_line2')} placeholder="Landmark, Area" />
      </div>
      <div className="addr-form__row addr-form__row--3">
        <div className="form-group">
          <label className="form-label">City *</label>
          <input className="form-input" value={addr.city} onChange={set('city')} placeholder="City" required />
        </div>
        <div className="form-group">
          <label className="form-label">State *</label>
          <select className="form-input form-select" value={addr.state} onChange={set('state')} required>
            <option value="">Select State</option>
            {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Pincode *</label>
          <input className="form-input" value={addr.pincode} onChange={set('pincode')} placeholder="6-digit" required />
        </div>
      </div>
      <div className="addr-form__actions">
        {showCancel && (
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        )}
        <button type="submit" className="btn btn-primary">
          <FiMapPin size={14} /> Save & Use This Address
        </button>
      </div>
    </form>
  );
};

// ─── Main CheckoutPage ────────────────────────────────────────────────────────
const CheckoutPage = () => {
  const { cart, totalPrice, clearCart } = useCart();
  const { user, loadUser } = useAuth();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [showNewAddr, setShowNewAddr] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);

  // Default to UPI (first online method)
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [couponCode, setCouponCode] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [note, setNote] = useState('');

  const items = cart?.items || [];
  const shipping = totalPrice >= 500 ? 0 : 90;
  const discount = couponData?.discount_amount || 0;
  const total = Number(totalPrice) - discount + shipping;

  const fetchAddresses = useCallback(async () => {
    setAddrLoading(true);
    try {
      const res = await authAPI.getAddresses();
      const data = res.data;
      const list = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []);
      setAddresses(list);
      if (list.length > 0) {
        const def = list.find(a => a.is_default) || list[0];
        setSelectedAddr(def);
        setShowNewAddr(false);
      } else {
        setShowNewAddr(true);
      }
    } catch {
      setAddresses([]);
      setShowNewAddr(true);
    } finally {
      setAddrLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchAddresses();
  }, [user, fetchAddresses]);

  const handleAuthComplete = async () => {
    await loadUser();
    await fetchAddresses();
  };

  const handleCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const res = await productAPI.validateCoupon({ code: couponCode, order_amount: totalPrice });
      setCouponData(res.data);
      toast.success(`Coupon applied! You save ₹${res.data.discount_amount}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid coupon');
      setCouponData(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSaveNewAddress = async (addrData) => {
    try {
      const res = await authAPI.addAddress({ ...addrData, is_default: addresses.length === 0 });
      const saved = res.data;
      setAddresses(prev => [...prev, saved]);
      setSelectedAddr(saved);
      setShowNewAddr(false);
      toast.success('Address saved!');
    } catch {
      toast.error('Could not save address');
    }
  };

  const handlePlaceOrder = async () => {
    if (items.length === 0) { toast.error('Cart is empty'); return; }
    if (!selectedAddr) { toast.error('Select a delivery address'); return; }
    setPlacing(true);

    const selectedOption = PAYMENT_OPTIONS.find(p => p.value === paymentMethod);
    const isCod = selectedOption?.isCod;

    try {
      const payload = {
        // All online methods map to 'razorpay' on backend
        payment_method: isCod ? 'cod' : 'razorpay',
        coupon_code: couponData ? couponCode : '',
        customer_note: note,
        address_id: selectedAddr.id,
      };

      // ── COD ──────────────────────────────────────────────────────────────
      if (isCod) {
        const res = await orderAPI.place(payload);
        await clearCart();
        toast.success('Order placed! 🎉');
        navigate(`/orders/${res.data.order_id}`);
        setPlacing(false);
        return;
      }

      // ── Razorpay (UPI / Card / Net Banking / EMI) ─────────────────────────
      const rzpRes = await orderAPI.createRazorpayOrder({ amount: total });
      const { razorpay_order_id, amount: rzpAmount, key } = rzpRes.data;

      const options = {
        key,
        amount: rzpAmount,
        currency: 'INR',
        order_id: razorpay_order_id,
        name: 'Vedonica International',
        description: 'Ayurvedic Wellness Products',
        image: '/logo192.png',
        // Pre-open the tab the user already selected
        method: selectedOption?.razorpayMethod || undefined,
        handler: async (response) => {
          try {
            const verify = await orderAPI.verifyRazorpayPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verify.data.success) {
              payload.razorpay_payment_id = response.razorpay_payment_id;
              payload.razorpay_order_id   = response.razorpay_order_id;
              const res = await orderAPI.place(payload);
              await clearCart();
              toast.success('Payment successful! 🎉');
              navigate(`/orders/${res.data.order_id}`);
            } else {
              toast.error('Payment verification failed');
            }
          } catch {
            toast.error('Something went wrong after payment');
          } finally {
            setPlacing(false);
          }
        },
        prefill: { name: user?.first_name, email: user?.email, contact: user?.phone },
        theme: { color: '#2d6a4f' },
        modal: { ondismiss: () => { toast.error('Payment cancelled'); setPlacing(false); } },
      };
      new window.Razorpay(options).open();

    } catch (err) {
      const errors = err.response?.data;
      if (typeof errors === 'object') {
        Object.values(errors).flat().forEach(e => toast.error(e));
      } else {
        toast.error('Failed. Please try again.');
      }
      setPlacing(false);
    }
  };

  // Empty cart guard
  if (items.length === 0) {
    return (
      <div className="checkout-empty page-wrapper">
        <div className="container">
          <div className="loading-container">
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '3rem', marginBottom: 16 }}>🛒</p>
              <h2 style={{ marginBottom: 8 }}>Your cart is empty</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>Add products before checking out.</p>
              <button className="btn btn-primary" onClick={() => navigate('/')}>Browse Products</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Checkout – Vedonica International</title></Helmet>
      <div className="checkout-page page-wrapper">
        <div className="container">
          <h1 className="checkout-page__title">Checkout</h1>
          <div className="checkout-layout">

            {/* ── Left Column ── */}
            <div className="checkout-main">

              {/* Guest Auth */}
              {!user && (
                <div className="checkout-section">
                  <GuestAuthFlow onAuthComplete={handleAuthComplete} />
                </div>
              )}

              {/* Delivery Address */}
              {user && (
                <div className="checkout-section">
                  <h2 className="checkout-section__title">
                    <FiMapPin size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Delivery Address
                  </h2>

                  {addrLoading ? (
                    <div className="addr-loading">
                      <span className="spinner-sm" style={{ borderColor: 'var(--green-primary)', borderTopColor: 'transparent', width: 24, height: 24, border: '3px solid' }} />
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 10 }}>Loading addresses...</span>
                    </div>
                  ) : (
                    <>
                      {addresses.length > 0 && !showNewAddr && (
                        <div className="addr-list">
                          {addresses.map(a => (
                            <div
                              key={a.id}
                              className={`addr-card ${selectedAddr?.id === a.id ? 'addr-card--selected' : ''}`}
                              onClick={() => setSelectedAddr(a)}
                            >
                              {selectedAddr?.id === a.id && (
                                <span className="addr-card__check"><FiCheck size={12} /></span>
                              )}
                              <p className="addr-card__name">{a.name} · {a.phone}</p>
                              <p className="addr-card__addr">
                                {a.address_line1}{a.address_line2 ? ', ' + a.address_line2 : ''}
                              </p>
                              <p className="addr-card__addr">{a.city}, {a.state} – {a.pincode}</p>
                              {a.is_default && (
                                <span className="badge badge-green" style={{ marginTop: 8 }}>Default</span>
                              )}
                            </div>
                          ))}
                          <button
                            className="addr-card addr-card--add"
                            onClick={() => { setShowNewAddr(true); setSelectedAddr(null); }}
                          >
                            <FiPlus size={20} />
                            <span>Add New Address</span>
                          </button>
                        </div>
                      )}

                      {(addresses.length === 0 || showNewAddr) && (
                        <div className="addr-form-wrap">
                          {showNewAddr && addresses.length > 0 && (
                            <button className="otp-back-btn" style={{ marginBottom: 16 }}
                              onClick={() => { setShowNewAddr(false); setSelectedAddr(addresses.find(a => a.is_default) || addresses[0]); }}>
                              <FiArrowLeft size={14} /> Use Saved Address
                            </button>
                          )}
                          <AddressForm
                            initial={{
                              name: user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
                              phone: user?.phone?.replace('+91', '') || '',
                            }}
                            showCancel={addresses.length > 0}
                            onCancel={() => { setShowNewAddr(false); setSelectedAddr(addresses.find(a => a.is_default) || addresses[0]); }}
                            onSave={handleSaveNewAddress}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Payment — only when logged in and address selected ── */}
              {user && selectedAddr && (
                <div className="checkout-section">
                  <h2 className="checkout-section__title">Payment Method</h2>

                  {/* Online options group */}
                  <p className="checkout-pm-group-label">Pay Online</p>
                  <div className="payment-options">
                    {PAYMENT_OPTIONS.filter(pm => !pm.isCod).map(pm => (
                      <div
                        key={pm.value}
                        className={`payment-option ${paymentMethod === pm.value ? 'payment-option--selected' : ''}`}
                        onClick={() => setPaymentMethod(pm.value)}
                      >
                        <span className="payment-option__icon">{pm.icon}</span>
                        <div className="payment-option__info">
                          <p className="payment-option__label">
                            {pm.label}
                            {pm.badge && <span className="checkout-pm-badge">{pm.badge}</span>}
                          </p>
                          <p className="payment-option__desc">{pm.desc}</p>
                        </div>
                        {paymentMethod === pm.value && (
                          <span className="payment-option__check"><FiCheck size={14} /></span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* COD — last */}
                  <p className="checkout-pm-group-label" style={{ marginTop: 16 }}>Other</p>
                  <div className="payment-options">
                    {PAYMENT_OPTIONS.filter(pm => pm.isCod).map(pm => (
                      <div
                        key={pm.value}
                        className={`payment-option ${paymentMethod === pm.value ? 'payment-option--selected' : ''}`}
                        onClick={() => setPaymentMethod(pm.value)}
                      >
                        <span className="payment-option__icon">{pm.icon}</span>
                        <div className="payment-option__info">
                          <p className="payment-option__label">{pm.label}</p>
                          <p className="payment-option__desc">{pm.desc}</p>
                        </div>
                        {paymentMethod === pm.value && (
                          <span className="payment-option__check"><FiCheck size={14} /></span>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="checkout-cod-note">COD may not be available for all pincodes</p>
                </div>
              )}

              {/* Order Note */}
              {user && selectedAddr && (
                <div className="checkout-section">
                  <h2 className="checkout-section__title">
                    Order Note{' '}
                    <span style={{ fontWeight: 400, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>(optional)</span>
                  </h2>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Any special instructions..."
                  />
                </div>
              )}
            </div>

            {/* ── Right: Order Summary ── */}
            <div className="checkout-sidebar">
              <div className="order-summary card">
                <h2 className="order-summary__title">Order Summary</h2>

                <div className="order-summary__items">
                  {items.map(item => (
                    <div key={item.id} className="order-summary__item">
                      <div className="order-summary__item-info">
                        <p className="order-summary__item-name">{item.product_name}</p>
                        <p className="order-summary__item-meta">
                          {item.variant?.pack_label} × {item.quantity}
                        </p>
                      </div>
                      <p className="order-summary__item-price">₹{Number(item.subtotal).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="divider" />

                {/* Coupon — only when logged in */}
                {user && (
                  <>
                    <div className="coupon-input">
                      <input
                        className="form-input coupon-input__field"
                        placeholder="Coupon code"
                        value={couponCode}
                        onChange={e => setCouponCode(e.target.value.toUpperCase())}
                      />
                      <button className="btn btn-outline coupon-input__btn" onClick={handleCoupon} disabled={couponLoading}>
                        <FiTag size={15} /> Apply
                      </button>
                    </div>
                    {couponData && (
                      <div className="coupon-applied">
                        <FiCheck size={14} /> Coupon applied! You save ₹{couponData.discount_amount}
                      </div>
                    )}
                    <div className="divider" />
                  </>
                )}

                {/* Totals */}
                <div className="order-summary__totals">
                  <div className="order-summary__row">
                    <span>Subtotal</span>
                    <span>₹{Number(totalPrice).toFixed(2)}</span>
                  </div>
                  <div className="order-summary__row">
                    <span>Shipping</span>
                    <span>
                      {shipping === 0
                        ? <span style={{ color: 'var(--green-primary)', fontWeight: 600 }}>FREE</span>
                        : `₹${shipping}`}
                    </span>
                  </div>
                  {discount > 0 && (
                    <div className="order-summary__row order-summary__row--green">
                      <span>Discount</span>
                      <span>–₹{Number(discount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="divider" style={{ margin: '12px 0' }} />
                  <div className="order-summary__row order-summary__row--total">
                    <span>Total</span>
                    <span>₹{total.toFixed(2)}</span>
                  </div>
                </div>

                <button
                  className="btn btn-primary order-summary__place"
                  onClick={handlePlaceOrder}
                  disabled={placing || !user || !selectedAddr}
                  title={!user ? 'Please login first' : !selectedAddr ? 'Please select an address' : ''}
                >
                  {placing ? 'Placing Order...' : `Place Order – ₹${total.toFixed(2)}`}
                </button>

                {!user && <p className="order-summary__hint">🔐 Login above to place your order</p>}
                {user && !selectedAddr && !addrLoading && (
                  <p className="order-summary__hint">📍 Add a delivery address to continue</p>
                )}

                <p className="order-summary__secure">🔒 Secure 256-bit SSL encrypted checkout</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPage;